import React, { useState } from 'react';

import { DndContext, DragOverlay, pointerWithin } from '@dnd-kit/core';
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core';

import { RepositoryIssue } from '@qality/common-model';
import { FlagService, FolderService } from '@qality/common-service';

import { useTestCaseRepositoryFoldersContext } from '../TestCaseRepositoryFoldersProvider';
import { Flags } from '../TestCaseRepositoryPage.const';
import { useTestCaseRepositorySelectionContext } from '../TestCaseRepositorySelectionProvider';
import { TestCaseRepositoryTableDragOverlayRow } from '../TestCaseRepositoryTable/rows/TestCaseRepositoryTableDragOverlayRow';
import {
  DraggableIssueData,
  DroppableFolderData,
  TestCaseRepositoryDndUtils,
} from './utils';

type Props = {
  children: React.ReactNode;
};

export function TestCaseRepositoryDnd({ children }: Props): JSX.Element {
  const {
    allSelectedIssuesIds,
    clearAllSelectedIssues,
    isAnyIssueSelected,
    isIssueSelectedAnywhere,
  } = useTestCaseRepositorySelectionContext();
  const { addToFolder } = useTestCaseRepositoryFoldersContext();

  const [draggedIssue, setDraggedIssue] = useState<RepositoryIssue>(null);

  const handleDragStart = (event: DragStartEvent) => {
    const { action, activeData } =
      TestCaseRepositoryDndUtils.identifyActionStart(event.active);

    switch (action) {
      case 'invalid': {
        return;
      }

      case 'drag-issue': {
        return handleDragIssue(activeData);
      }

      default: {
        return;
      }
    }
  };

  const handleDragEnd = async (event: DragEndEvent): Promise<void> => {
    const { action, activeData, overData } =
      TestCaseRepositoryDndUtils.identifyActionEnd(event.active, event.over);

    switch (action) {
      case 'invalid': {
        return;
      }

      case 'dnd-issue-to-folder': {
        return handleDndIssueToFolder(activeData, overData);
      }

      default: {
        return;
      }
    }
  };

  const isBulkDraggingIssues = () => {
    return isAnyIssueSelected;
  };

  const isDraggedIssueInBulk = (issueData: DraggableIssueData) => {
    return isIssueSelectedAnywhere(issueData.payload.issue);
  };

  const handleDragIssue = (issueData: DraggableIssueData) => {
    setDraggedIssue(issueData.payload.issue);

    const isDraggedIssueOutOfBulk =
      isBulkDraggingIssues() && !isDraggedIssueInBulk(issueData);

    if (isDraggedIssueOutOfBulk) {
      clearAllSelectedIssues();
    }
  };

  const handleDndIssueToFolder = async (
    issueData: DraggableIssueData,
    folderData: DroppableFolderData
  ) => {
    if (folderData.payload.isDisabled) {
      FlagService.createWarningFlag(
        'Error',
        Flags.addTestCaseToFolder.warning()
      );
      return;
    }

    const isBulk = isBulkDraggingIssues();
    const numberOfTestCases = isBulk ? allSelectedIssuesIds.length : 1;
    const draggedIssuesIds = isBulk
      ? allSelectedIssuesIds
      : [issueData.payload.issue.id];

    const droppedFolderId = folderData.payload.folder.id;

    try {
      const { data: newTestCasesInFolder } =
        await FolderService.addTestCasesToFolder(
          draggedIssuesIds,
          droppedFolderId
        );

      addToFolder(droppedFolderId, newTestCasesInFolder);

      FlagService.createSuccessFlag(
        'Success',
        Flags.addTestCaseToFolder.success(numberOfTestCases)
      );
    } catch (error) {
      FlagService.createErrorFlag(
        'Error',
        Flags.addTestCaseToFolder.error(numberOfTestCases)
      );
    } finally {
      setDraggedIssue(null);
    }
  };

  return (
    <DndContext
      collisionDetection={pointerWithin}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      {children}
      <DragOverlay dropAnimation={null}>
        {draggedIssue && (
          <TestCaseRepositoryTableDragOverlayRow
            issue={draggedIssue}
            numberOfItems={allSelectedIssuesIds.length}
          />
        )}
      </DragOverlay>
    </DndContext>
  );
}
